import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { IOutlet } from '../../../main/entities';
import { transl } from '../../../main/transl';
import { mg } from '../../../mg/mg';
import { useBooleanStateAgg, useStringStateAgg } from '../../../mg/useStateAgg';
import { toasterSvc } from '../../../services/ToasterSvc';
import { useOmnistore } from '../../../stores/Omnistore';

const useStyles = makeStyles({
	EditOutletDialog: {
	},
	content: {
		margin: 10,
		display: 'grid',
		gridTemplateColumns: 'auto 1fr',
		gap: '10px',
		alignItems: 'center',
	},
	name: {
		fontWeight: 'bold',
	},
	chips: {
		paddingTop: 20,
		paddingBottom: 20,
		display: 'flex',
		flexWrap: 'wrap',
	},
	autocompleteRow: {
		display: 'grid',
		gridTemplateColumns: '1fr auto',
		alignItems: 'stretch',
	},
	chip: {
		margin: 10,
	},
	addNewCategory: {
		marginLeft: 'auto',
	},
	addCategoryRow: {
		display: 'grid',
		gridTemplateColumns: '1fr auto',
		alignItems: 'center',
	},
	input: {
		position: 'relative',
		bottom: '0.6em',
	},
});

interface IEditOutletDialogProps {
	onClose() : void
	outlet: IOutlet
}

export const EditOutletDialog = observer(function EditOutletDialog(props: IEditOutletDialogProps) {
	const classes = useStyles();
	const {onClose, outlet} = props;
	const stringToList = (s: string) => s.split(';').map(s => s.trim().replace(/\s+/g, ' ')).filter(s => s);
	const facebookUrlAgg = useStringStateAgg(outlet.facebookUrl, 'facebookUrl');
	const homepageUrlAgg = useStringStateAgg(outlet.homepageUrl, 'homepageUrl');
	const [categories, setCategories] = useState(new Set(stringToList(outlet.categories)));
	const newCategoryAgg = useStringStateAgg('');
	const showNewCategoriesAgg = useBooleanStateAgg();
	const omnistore = useOmnistore();
	const outletRepo = omnistore.multirepo.outletRepo;
	const allCategoriesSet = new Set(outletRepo.allAsList.flatMap(o => stringToList(o.categories)));
	for (const s of categories) allCategoriesSet.add(s);
	const allCategories = [...allCategoriesSet].sort(mg.caseHalfSensitiveCompare);
	const communicator = omnistore.communicator;
	const removeCategory = (s: string) => {
		const newCategories = new Set(categories);
		newCategories.delete(s);
		setCategories(newCategories);
	};
	const onAutocompleteChange = (e: unknown, value: string|null) => {
		showNewCategoriesAgg.falseHandler();
		if (!value) return;
		const newCategories = new Set(categories);
		newCategories.add(value);
		setCategories(newCategories);
		newCategoryAgg.setter('');
	};
	const onAutocompleteInputChange = (e: unknown, value: string) => {
		newCategoryAgg.setter(value);
	};
	const save = () => {
		const param = {
			...outlet,
			facebookUrl: facebookUrlAgg.value,
			homepageUrl: homepageUrlAgg.value,
			categories: [...categories, newCategoryAgg.value].join(';'),
		};
		communicator.doPost('UpdateOutlet', param)
			.then(toasterSvc.handleResponse('saved'))
			.then(r => {
				if (r.failure) return;
				onClose();
				outletRepo.fetch('DIRECT');
			});
	};

	return <Dialog className={classes.EditOutletDialog} fullWidth open={true}>
		<DialogTitle>{transl('EditOutletDialog')}</DialogTitle>
		<DialogContent className={classes.content}>
			{
				['name', 'ownerEmail', 'zipcode'].map(s => <React.Fragment key={s}>
					<div className={classes.name}>{transl(s)}</div>
					<div>{outlet[s]}</div>
				</React.Fragment>)
			}
			<div className={classes.name}>{transl('categories')}</div>
			<div>
				<div className={classes.chips}>
					{allCategories.filter(s => categories.has(s)).map((s, i) =>
						<Chip key={s} className={classes.chip} label={s} onDelete={() => removeCategory(s)}/>)}
					{showNewCategoriesAgg.value ? null :
						<IconButton className={classes.addNewCategory} onClick={showNewCategoriesAgg.trueHandler}><AddIcon/></IconButton>}
				</div>
				{!showNewCategoriesAgg.value ? null :
					<div className={classes.autocompleteRow}>
						<Autocomplete autoComplete freeSolo includeInputInList blurOnSelect
							open={true}
							options={allCategories.filter(s => !categories.has(s))}
							renderInput={(params) => <TextField {...params} autoFocus margin="normal"/>}
							onChange={onAutocompleteChange}
							onInputChange={onAutocompleteInputChange}
						/>
						<IconButton className={classes.addNewCategory} onClick={() => onAutocompleteChange(null, newCategoryAgg.value)}>
							{newCategoryAgg.value ? <AddIcon/> : <CloseIcon/>}
						</IconButton>
					</div>
				}
			</div>
			{
				[facebookUrlAgg, homepageUrlAgg].map(a => <React.Fragment key={a.name}>
					<div className={classes.name}>{transl(a.name)}</div>
					<TextField key={a.name}
						className={classes.input}
						label={transl(a.name)}
						value={a.value}
						onChange={a.changeHandler}
					/>
				</React.Fragment>)
			}
		</DialogContent>
		<DialogActions>
			<Button onClick={onClose}>{transl('cancel')}</Button>
			<Button color="primary" onClick={save}>{transl('save')}</Button>
		</DialogActions>
	</Dialog>;
});
